import type { NoteRecord } from '@/components/NoteCard';
import { authorAccent, projectAccent } from '@/lib/deterministic-color';

/**
 * Notes redesign — group bucketing.
 *
 * Three group modes per Q4:
 *   - `recency`  → Today / This week / Earlier (Pinned bucket lives
 *                  behind the deferred pin feature, see Q5b — kept
 *                  visible-but-inert in the bucket order until pinning
 *                  is wired)
 *   - `project`  → one section per project parent. Task-parented
 *                  notes group under their project too (the workspace
 *                  picture; the project page hides this option).
 *   - `author`   → one section per `created_by` user.
 *
 * Headers are rendered by `NotesGroupHeader` — this module just
 * computes the buckets and supplies the visual props (label, glyph
 * shape + color, count + sub-label).
 *
 * Within each bucket, the caller pre-sorts by `recencyMinutes`
 * ascending (newest first); buckets are output in the canonical
 * order documented per mode.
 */

export type GroupMode = 'recency' | 'project' | 'author';

export interface NotesGroup {
  /** Stable identity for `key=` so re-grouping doesn't churn. */
  key: string;
  /** Display label. */
  label: string;
  /** Inline sub-label (e.g. `"3 notes"`). */
  sub: string;
  /** Glyph shape for the header — circle by default, rounded square
   *  for the future "Pinned" bucket (deferred). */
  glyphShape: 'circle' | 'square';
  /** Glyph color (CSS color string, OKLCH from `deterministic-color`). */
  glyphColor: string;
  items: NoteRecord[];
}

const noteCountSub = (n: number): string => `${n} note${n === 1 ? '' : 's'}`;

/**
 * Bucket recency by time-since-update. Same thresholds as the
 * prototype: `< 1 day` → Today, `< 7 days` → This week, else
 * Earlier. Empty buckets are dropped (so an old workspace with only
 * "Earlier" notes doesn't show three empty headers).
 */
const groupByRecency = (notes: NoteRecord[]): NotesGroup[] => {
  const today: NoteRecord[] = [];
  const week: NoteRecord[] = [];
  const earlier: NoteRecord[] = [];
  for (const n of notes) {
    const days = n.recencyMinutes / (60 * 24);
    if (days < 1) today.push(n);
    else if (days < 7) week.push(n);
    else earlier.push(n);
  }
  const out: NotesGroup[] = [];
  if (today.length > 0) {
    out.push({
      key: 'today',
      label: 'Today',
      sub: noteCountSub(today.length),
      glyphShape: 'circle',
      glyphColor: 'oklch(0.7 0.16 245)',
      items: today,
    });
  }
  if (week.length > 0) {
    out.push({
      key: 'week',
      label: 'This week',
      sub: noteCountSub(week.length),
      glyphShape: 'circle',
      glyphColor: 'oklch(0.65 0.06 245)',
      items: week,
    });
  }
  if (earlier.length > 0) {
    out.push({
      key: 'earlier',
      label: 'Earlier',
      sub: noteCountSub(earlier.length),
      glyphShape: 'circle',
      glyphColor: 'oklch(0.6 0 0)',
      items: earlier,
    });
  }
  return out;
};

/**
 * Bucket by parent project. Notes attached to a task fall under the
 * task's project (since tasks belong to projects). Notes without a
 * project (workspace-scoped notes if any) are bucketed as
 * "(no project)" — defensive; the current API doesn't allow it.
 */
const groupByProject = (notes: NoteRecord[]): NotesGroup[] => {
  const map = new Map<string, { name: string; items: NoteRecord[] }>();
  for (const n of notes) {
    const slug = n.projectSlug ?? '__none__';
    const existing = map.get(slug);
    const name = n.projectName ?? '(no project)';
    if (existing) existing.items.push(n);
    else map.set(slug, { name, items: [n] });
  }
  return [...map.entries()].map(([slug, { name, items }]) => ({
    key: `p-${slug}`,
    label: name,
    sub: noteCountSub(items.length),
    glyphShape: 'circle' as const,
    glyphColor: projectAccent(slug === '__none__' ? null : slug),
    items,
  }));
};

/**
 * Bucket by author. Author group glyph color is hash-derived from
 * the user id (deterministic, distinct from project hue palette).
 */
const groupByAuthor = (notes: NoteRecord[]): NotesGroup[] => {
  const map = new Map<string, { label: string; items: NoteRecord[] }>();
  for (const n of notes) {
    const id = n.authorId ?? '__none__';
    const label = n.authorName ?? n.authorEmail ?? 'Unknown';
    const existing = map.get(id);
    if (existing) existing.items.push(n);
    else map.set(id, { label, items: [n] });
  }
  return [...map.entries()].map(([id, { label, items }]) => ({
    key: `a-${id}`,
    label,
    sub: noteCountSub(items.length),
    glyphShape: 'circle' as const,
    glyphColor: authorAccent(id === '__none__' ? null : id),
    items,
  }));
};

export const groupNotes = (notes: NoteRecord[], mode: GroupMode): NotesGroup[] => {
  if (mode === 'project') return groupByProject(notes);
  if (mode === 'author') return groupByAuthor(notes);
  return groupByRecency(notes);
};
